import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import InputField from "components/Site/Common/Form/ReactHookForm/InputField";
import DateInput from "components/Site/Common/Form/ReactHookForm/DateInput";
import CheckboxWithLabel from "components/Site/Common/Form/ReactHookForm/CheckboxWithLabel";
import { useModal } from "src/hooks/useModal";
import { formatPhoneNumber } from "src/utils/common";
import { getProductFromSessionStorage } from "src/utils/storage/auth";
import { Dispatch, SetStateAction } from "react";
import dayjs from "dayjs";
import RightDetailBox from "../_RightDetailBox";
import SelectField from "components/Site/Common/Form/ReactHookForm/SelectField";
import { createServicePromotionAskV2 } from "src/service/pay";
import { notifyError, notifySuccess } from "src/components/Common/Toast";
import { useRouter } from "src/hooks/useRouter";
import { useTranslation } from "react-i18next";
// import { IssueAppType } from "src/service/types/pay.type";

type IssueAppType = {
  appName: string;
  appVersion: string;
};
type FormValues = {
  companyName: string;
  startDate: string;
  endDate: string;
  externalLicenseCount: number;
  licenseCount: number;
  product: string;
  purpose: string;
  telNumber: string;
  userName: string;
  userPerStorage: number;
  issueAppList: IssueAppType[];
  confirmPolicy: boolean;
};
// 🧩 Yup 스키마 정의
const schema = yup.object({
  companyName: yup.string().required("admin.common.alert.check.enterCompany"),
  startDate: yup.string().notRequired(),
  endDate: yup.string().notRequired(),
  // externalLicenseCount: yup.number().required(),
  // licenseCount: yup.number().min(10).required(),
  licenseCount: yup.number().required(),
  product: yup.string().required(),
  purpose: yup.string().notRequired(),
  telNumber: yup.string().required("siteType.free.detail.alertMsg5"), // .matches(/^[0-9]{2,3}-[0-9]{3,4}-[0-9]{4}$/, ""),
  userName: yup
    .string()
    .required("logIn.enterName") // 이름을 입력해주세요
    .max(50, "siteType.free.detail.alertMsg1"), // 50자까지 입력할 수 있습니다
  // userPerStorage: yup.number().min(0).required(),
  issueAppList: yup
    .array()
    .of(
      yup.object({
        id: yup.string().required(),
        name: yup.string().required(),
      })
    )
    .notRequired(),
  // ✅ 여기 추가
  confirmPolicy: yup
    .boolean()
    .oneOf([true], "logIn.signUp.agreement") // 이용약관 및 개인정보처리방침에 동의해주세요.
    .required(),
  packageName: yup.string().required(),
  // .oneOf([true], t("siteType.info"))
});
interface IAskPromotionProps {
  setToggleAskPromotionOn: Dispatch<SetStateAction<boolean>>;
}
const AskPromotionNew = ({ setToggleAskPromotionOn }: IAskPromotionProps) => {
  type FormValues = yup.InferType<typeof schema>;
  const route = useRouter();
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { isValid, errors },
  } = useForm<FormValues>({
    defaultValues: {
      product: getProductFromSessionStorage(),
      licenseCount: 10,
      packageName: "DOCUMENT_SECURITY",
      startDate: dayjs().format("YYYY-MM-DD"), // "2024-01-01", // ISO 8601 형식 문자열
      endDate: dayjs().add(1, "year").format("YYYY-MM-DD"), // "2024-01-31",
    },
    resolver: yupResolver(schema),
    mode: "onChange",
    criteriaMode: "all", // 모든 유효성 검사 규칙을 확인
  });
  const onSubmit = async (data: FormValues) => {
    // console.log(data);
    // const {} = data;
    await createServicePromotionAskV2({
      product: data?.product,
      userName: data.userName,
      telNumber: formatPhoneNumber(data.telNumber),
      companyName: data.companyName,
      purpose: data?.purpose ?? "",
      licenseCount: data?.licenseCount,
      startDate: data?.startDate ?? "",
      endDate: data?.endDate ?? "",
      packageName: data?.packageName,
    })
      .then((result) => {
        notifySuccess(t("siteType.promotionApply.title"));
        setToggleAskPromotionOn((prev) => !prev);
        route.goSitePromotion({});
      })
      .catch((error: any) => {
        if (error?.errorCode && error?.errorMessage) {
          notifyError(error.errorMessage);
        }
      });
  };
  const { openTermsService, openPrivatePolicy, openPaidTermsService } =
    useModal();
  // useGetDrmPackageList({});

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="form">
      <div className="flex justify-space-between">
        <div className="flex-item box box__lt bg-white">
          <h4 className="form-title">
            {
              t("siteType.free.detail.title") // 사용자 정보
            }
          </h4>
          <InputField
            register={register}
            name="userName"
            label={t("siteType.free.detail.username")}
            labelRight={
              <span
                className="form-item-question cursor-pointer"
                onClick={() => setToggleAskPromotionOn((prev) => !prev)}
              >
                {
                  t("siteType.premium.detail.plan.haveCode") // 프리미엄 코드가 있으신가요?
                }
              </span>
            }
            required={true}
            errors={errors}
          />
          <InputField
            register={register}
            name="telNumber"
            label={t("siteType.free.detail.phoneNumber")}
            onChange={(e) => {
              e.target.value = formatPhoneNumber(e.target.value);
            }}
            required={true}
            errors={errors}
          />
          <InputField
            register={register}
            name="companyName"
            label={t("siteType.free.detail.company")}
            required={true}
            errors={errors}
          />
          <InputField
            register={register}
            name="purpose"
            label={t("siteType.free.detail.purpose")}
            errors={errors}
            type="textarea"
          />
          <h4 className="form-title">
            {/* 요금제 세부 정보 */}
            {t("siteType.premium.detail.plan.title")}
          </h4>
          <SelectField
            label="Package"
            register={register}
            required={true}
            name="packageName"
            options={[
              {
                label: t("siteType.premium.detail.plan.package.dsp_1"),
                value: "DOCUMENT_SECURITY",
              },
              {
                label: t("siteType.premium.detail.plan.package.dsp_2"),
                value: "PRINT_SECURITY",
              },
              {
                label: t("siteType.premium.detail.plan.package.dsp_3"),
                value: "SCREEN_SECURITY",
              },
              {
                label: t("siteType.premium.detail.plan.package.dsp_4"),
                value: "DOCUMENT_DR",
              },
              {
                label: t("siteType.premium.detail.plan.package.dsp_5"),
                value: "DOCUMENT_SECURITY_PLUS_PACK",
              },
              {
                label: t("siteType.premium.detail.plan.package.dsp_6"),
                value: "DOCUMENT_SECURITY_DR_PACK",
              },
              {
                label: t("siteType.premium.detail.plan.package.dsp_7"),
                value: "PRIVACY_PACK",
              },
              {
                label: t("siteType.premium.detail.plan.package.dsp_8"),
                value: "ALL_IN_ONE_DATA_SECURITY_PACK",
              },
            ]}
          />
          <div className="form-item">
            <span className="form-item-title">
              {t("siteType.basic.detail.plan.userNum")}
            </span>
            <input
              {...register("licenseCount")}
              type="number"
              className="form-control custom-form width max-w-110"
              min={10}
              max={9999}
            />
          </div>
          {/* 바로 적용안됨 왜? */}
          {/* <InputField
            register={register}
            name="licenseCount"
            type="number"
            required={true}
            min={10}
            label={t("siteType.basic.detail.plan.userNum")}
            addClassName="width max-w-110"
            errors={errors}
          /> */}
          {/* 구독 희망 기간 */}
          <div className="form-item">
            <span className="form-item-title">
              {t("siteType.premium.detail.plan.period")}
            </span>
            <div className="flex align-bottom input_date">
              <DateInput
                control={control}
                name="startDate"
                label={t("siteType.premium.detail.plan.startDate")}
              />
              <DateInput
                control={control}
                name="endDate"
                label={t("siteType.premium.detail.plan.endDate")}
              />
            </div>
          </div>
          <CheckboxWithLabel
            id="confirmPolicy"
            name="confirmPolicy"
            register={register}
            errors={errors}
            // infoText={t("siteType.info")}
            labelContent={
              <>
                {t("siteType.agreement1")}
                <a onClick={openTermsService} className="cursor-pointer">
                  <b className="text text--black">{t("siteType.agreement2")}</b>
                </a>
                {t("siteType.agreement3")}
                <a onClick={openPrivatePolicy} className="cursor-pointer">
                  <b className="text text--black">{t("siteType.agreement4")}</b>
                </a>
                {window.sessionStorage.getItem("product") ===
                  "WRAPSODY_ECO" && (
                  <>
                    {t("siteType.agreement5")}
                    <a
                      onClick={openPaidTermsService}
                      className="cursor-pointer"
                    >
                      <b className="text text--black">
                        {t("siteType.agreement6")}
                      </b>
                    </a>
                  </>
                )}
                {t("siteType.agreement7")}
              </>
            }
          />
        </div>
        <RightDetailBox
          toggleAskPromotionOn={true}
          licenseCount={watch("licenseCount") || 0}
          startDate={watch("startDate") || ""}
          endDate={watch("endDate") || ""}
          isCheckAskPromotion={isValid}
        />
      </div>
      {/* <button
        type="submit"
        className="bg-green-500 text-white px-4 py-2 rounded"
      >
        제출
      </button> */}
    </form>
  );
};

export default AskPromotionNew;
